import { useQuery, useMutation, useQueryClient, UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import { contractorApi, Contractor, ContractorFormData } from 'shared/services/contractorApi';
import { queryKeys } from '../queryConfig';

/**
 * Filter parameters for contractors list query
 */
export interface ContractorsFilters {
  search?: string;
  status?: string;
  companyId?: string;
  page?: number;
  limit?: number;
}

/**
 * Pagination info returned from API
 */
interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/**
 * Response structure from contractors API (transformed by query)
 */
interface ContractorsResponse {
  success: boolean;
  data: Contractor[];
  pagination: PaginationInfo;
  error?: string;
}

/**
 * Hook to fetch contractors with filters and pagination
 *
 * Features:
 * - Automatic caching (5 min TTL)
 * - Deduplication (multiple components share same request)
 * - Background refetching when stale
 * - Previous data shown while fetching new data (keepPreviousData)
 *
 * @param filters - Filter parameters for the query
 * @returns React Query result with contractors data, loading state, error state
 */
export const useContractors = (
  filters: ContractorsFilters = {}
): UseQueryResult<ContractorsResponse, Error> => {
  const queryParams = {
    page: filters.page || 1,
    limit: filters.limit || 10,
    ...(filters.search && { search: filters.search }),
    ...(filters.status && filters.status !== 'all' && { status: filters.status }),
    ...(filters.companyId && filters.companyId !== 'all' && { companyId: filters.companyId }),
  };

  return useQuery({
    queryKey: queryKeys.contractors.list(queryParams, filters.companyId || ''),
    queryFn: async () => {
      const response = await contractorApi.getAll(queryParams);

      if (!response.success) {
        throw new Error(response.message || 'Error al cargar contratistas');
      }

      return {
        ...response,
        data: response.data || [],
        pagination: response.pagination || {
          total: (response.data || []).length,
          page: queryParams.page,
          limit: queryParams.limit,
          pages: Math.ceil((response.data || []).length / queryParams.limit),
        },
      } as ContractorsResponse;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: 1,
  });
};

/**
 * Hook to delete a contractor
 *
 * On success, invalidates all contractor queries to refetch fresh data.
 */
export const useDeleteContractor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contractorApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contractors.all });
    },
  });
};

/**
 * Hook to create a new contractor
 *
 * On success, invalidates all contractor queries to refetch fresh data.
 */
export const useCreateContractor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ContractorFormData) => contractorApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contractors.all });
    },
  });
};

/**
 * Hook to update a contractor
 *
 * On success, invalidates all contractor queries to refetch fresh data.
 */
export const useUpdateContractor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ContractorFormData> }) =>
      contractorApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contractors.all });
    },
  });
};
